// Client-side thumbnail capture from a recording (wiki/feature-my-library.md
// §"Thumbnails"). A frame is grabbed from a recording Blob via an offscreen
// <video> + <canvas>. The automatic shot is a frame from a RANDOM moment in the
// 2s–5s window of the take, skipping near-black / blank frames; the caller can
// also grab a frame at a specific time (the scrub-to-replace affordance) or just
// hand in an uploaded image.
//
// Pure browser code — guarded so it no-ops (resolves null) under SSR / a test
// env with no <video> decode. Produces a JPEG Blob (small, universally
// supported) sized to a card-thumbnail footprint.

/** Output thumbnail max edge in px — a card-sized image, not the full frame. */
const MAX_EDGE = 640;

/** JPEG quality for the encoded thumbnail. */
const JPEG_QUALITY = 0.8;

/** The image MIME the capture produces. */
export const THUMBNAIL_CONTENT_TYPE = 'image/jpeg';

/** Below this mean luma (0–255) a frame is treated as near-black / blank. */
const MIN_MEAN_LUMA = 18;

/** True when the runtime can decode video into a canvas (browser, not SSR). */
export function canCaptureThumbnail(): boolean {
  return (
    typeof document !== 'undefined' &&
    typeof URL !== 'undefined' &&
    typeof URL.createObjectURL === 'function'
  );
}

function loadVideo(blob: Blob): Promise<HTMLVideoElement> {
  return loadVideoFromSrc(URL.createObjectURL(blob), false);
}

/**
 * Load a decodable <video> from a URL (an uploaded take's signed R2 GET). The
 * bytes are cross-origin, so we set `crossOrigin='anonymous'` BEFORE assigning
 * `src` — without it the canvas taints and `toBlob` throws. The signed GET must
 * be served with CORS allowing the web origin (the R2 bucket's CORS config); a
 * missing header fails the load (error event) and the capture resolves null.
 */
function loadVideoFromUrl(url: string): Promise<HTMLVideoElement> {
  return loadVideoFromSrc(url, true);
}

/**
 * Shared loader: attach a muted <video>, resolve on `loadedmetadata` (enough to
 * seek), reject on decode error. `crossOrigin` is set for a remote URL so the
 * canvas stays readable; a blob src is same-origin and needs none.
 */
function loadVideoFromSrc(src: string, crossOrigin: boolean): Promise<HTMLVideoElement> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.muted = true;
    video.preload = 'auto';
    if (crossOrigin) video.crossOrigin = 'anonymous';
    video.src = src;
    const onLoaded = (): void => {
      cleanup();
      resolve(video);
    };
    const onError = (): void => {
      cleanup();
      releaseVideo(video);
      reject(new Error('Could not decode the recording for a thumbnail.'));
    };
    function cleanup(): void {
      video.removeEventListener('loadedmetadata', onLoaded);
      video.removeEventListener('error', onError);
    }
    video.addEventListener('loadedmetadata', onLoaded);
    video.addEventListener('error', onError);
  });
}

/** Detach a decode <video> and revoke its object-URL (a no-op for a plain URL). */
function releaseVideo(video: HTMLVideoElement | null): void {
  if (!video?.src) return;
  const src = video.src;
  video.src = '';
  try {
    URL.revokeObjectURL(src);
  } catch {
    /* ignore */
  }
}

function seekTo(video: HTMLVideoElement, time: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const onSeeked = (): void => {
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
      resolve();
    };
    const onError = (): void => {
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
      reject(new Error('Could not seek the recording for a thumbnail.'));
    };
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('error', onError);
    video.currentTime = time;
  });
}

/** Draw the current video frame onto a card-sized canvas. */
function drawFrame(video: HTMLVideoElement): HTMLCanvasElement | null {
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (!w || !h) return null;
  const scale = Math.min(1, MAX_EDGE / Math.max(w, h));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(w * scale));
  canvas.height = Math.max(1, Math.round(h * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas;
}

/** Mean luma (0–255) of a canvas — used to skip near-black / blank frames. */
function meanLuma(canvas: HTMLCanvasElement): number {
  const ctx = canvas.getContext('2d');
  if (!ctx) return 0;
  let data: Uint8ClampedArray;
  try {
    data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  } catch {
    // A tainted canvas (shouldn't happen for a same-origin blob) — assume OK.
    return 255;
  }
  let sum = 0;
  const px = data.length / 4;
  // Sample every 16th pixel — luma is cheap enough but we don't need every one.
  let counted = 0;
  for (let i = 0; i < data.length; i += 4 * 16) {
    sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    counted += 1;
  }
  return counted > 0 ? sum / counted : sum / px;
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), THUMBNAIL_CONTENT_TYPE, JPEG_QUALITY);
  });
}

/** Seek a loaded video to `time` (clamped inside the take), draw + encode a JPEG. */
async function captureFrameFromVideo(video: HTMLVideoElement, time: number): Promise<Blob | null> {
  const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0;
  const clamped = duration > 0 ? Math.min(Math.max(0, time), duration - 0.05) : 0;
  await seekTo(video, clamped);
  const canvas = drawFrame(video);
  if (!canvas) return null;
  return await canvasToBlob(canvas);
}

/**
 * A random moment in the take's 2s–5s window — past the camera warm-up (a frame
 * at t=0 is the sensor still exposing), still the take's opening.
 *
 * `duration` is the decoded duration, which a MediaRecorder webm reports as
 * non-finite: its container header declares none (see `probeMediaDuration`). An
 * unknown duration therefore KEEPS the window — the seek resolves off the
 * buffered bytes even when the container is silent about length. A known short
 * take clamps just inside its end so the seek never runs past it.
 */
function pickCaptureTime(duration: number): number {
  const time = 2 + Math.random() * 3;
  return Number.isFinite(duration) && duration > 0
    ? Math.min(time, Math.max(0, duration - 0.05))
    : time;
}

/**
 * Auto-pick the shot: a frame from a random moment in the 2s–5s window. Two
 * independent draws from the window, so a seek that fails or lands on a
 * near-black / blank frame gets a second chance; the last resort is whatever
 * frame the video currently holds. Resolves null when capture is unsupported or
 * the recording can't be decoded at all.
 */
export async function autoCaptureThumbnail(blob: Blob): Promise<Blob | null> {
  if (!canCaptureThumbnail()) return null;
  let video: HTMLVideoElement | null = null;
  try {
    video = await loadVideo(blob);
    let decodable: HTMLCanvasElement | null = null;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        await seekTo(video, pickCaptureTime(video.duration));
      } catch {
        continue;
      }
      const canvas = drawFrame(video);
      if (!canvas) continue;
      decodable = canvas;
      if (meanLuma(canvas) >= MIN_MEAN_LUMA) return await canvasToBlob(canvas);
    }
    const chosen = decodable ?? drawFrame(video);
    return chosen ? await canvasToBlob(chosen) : null;
  } catch {
    return null;
  } finally {
    releaseVideo(video);
  }
}

/**
 * Capture a single frame at `time` seconds from a recording Blob (the
 * scrub-to-replace affordance for an on-device take). Resolves null when
 * capture is unsupported or the seek fails.
 */
export async function captureFrameAt(blob: Blob, time: number): Promise<Blob | null> {
  if (!canCaptureThumbnail()) return null;
  let video: HTMLVideoElement | null = null;
  try {
    video = await loadVideo(blob);
    return await captureFrameFromVideo(video, time);
  } catch {
    return null;
  } finally {
    releaseVideo(video);
  }
}

/**
 * Capture a single frame at `time` seconds from a recording URL — the
 * scrub-to-replace affordance for an UPLOADED take, whose bytes live only on the
 * server (signed R2 GET), not in the local cache. Same seek/draw path as
 * {@link captureFrameAt}; the video loads cross-origin (see
 * {@link loadVideoFromUrl}). Resolves null when capture is unsupported, the
 * cross-origin read is blocked (no bucket CORS → tainted canvas / load error),
 * or the seek fails.
 */
export async function captureFrameAtUrl(url: string, time: number): Promise<Blob | null> {
  if (!canCaptureThumbnail()) return null;
  let video: HTMLVideoElement | null = null;
  try {
    video = await loadVideoFromUrl(url);
    return await captureFrameFromVideo(video, time);
  } catch {
    return null;
  } finally {
    releaseVideo(video);
  }
}
